'use client';

import {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';

import not from 'ramda/src/not';
import isNil from 'ramda/src/isNil';

import { WAG_CONTENT_API_URL } from '../../../constants/endpoints';
import { getArticleVetAdviceListTransformer } from './getArticleVetAdviceListTransformer';
import { UnpackRecord } from '../../../types';
import { HealthArticleResponse } from '../../../types/api';
import { HealthArticleUGCVirtualListValue } from '../../../components/HealthArticleUGCVirtualList';
import { HealthArticleSectionHealthExperienceListValue } from '../../../components/HealthArticleSectionHealthExperienceList';
import { useApiQuery } from '../../../wag-react/common/api/hooks/useApiQuery';

// Raw response
type QueryResponseData = Record<'next_page' | 'prev_page', null | number> &
  Omit<UnpackRecord<Pick<HealthArticleResponse, 'veterinary_advice'>>, 'title'>;

// Transformed/consumable data
type QueryData = Partial<Record<'nextPage' | 'prevPage', null | number>> & {
  hasNextPage: boolean;
  adviceList: HealthArticleUGCVirtualListValue;
  healthExperienceList: HealthArticleSectionHealthExperienceListValue;
};
type Args = {
  url: string;
  initialData: QueryData;
  page?: number;
  articleType: string; // 'Condition | 'Symptom' | 'Treatment'
  slug: string;
};

export const useGetArticleVetAdviceList = ({
  url,
  initialData,
  articleType,
  slug,
  page: initialPage = 1,
}: Args) => {
  const isFirstRender = useRef(true);
  const [page, setPage] = useState(initialPage);
  const [data, setData] = useState<QueryData>(initialData);
  const {
    error,
    isLoading,
    data: queryData,
    query,
  } = useApiQuery<
    QueryResponseData
  >(url, {
    baseURL: WAG_CONTENT_API_URL,
  });
  const getArticleVetAdviceList = useCallback(() => {
    query({
      queryParams: {
        page,
      },
      pathParams: {
        articleType,
        slug,
      },
    });
  }, [query, articleType, slug, page]);

  useEffect(() => {
    // ! Don't fetch on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    getArticleVetAdviceList();
  }, [page, getArticleVetAdviceList]);

  useEffect(() => {
    if (!queryData) {
      return;
    }

    const transformedData = getArticleVetAdviceListTransformer(queryData);
    setData((prevData) => ({
      ...prevData,
      nextPage: transformedData.nextPage,
      prevPage: transformedData.prevPage,
      hasNextPage: not(isNil(transformedData.nextPage)),
      adviceList: [...prevData.adviceList, ...transformedData.adviceList],
      healthExperienceList: [
        ...prevData.healthExperienceList,
        ...transformedData.healthExperienceList,
      ],
    }));
  }, [queryData]);

  const execute = useCallback(() => {
    setPage((currentPage) => (Number.isNaN(currentPage) ? 1 : currentPage + 1));
  }, []);

  return {
    data,
    error,
    isLoading,
    execute,
  };
};
